import React, { useState } from 'react';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const FieldSelectionPanel = ({ 
  availableFields, 
  selectedFields, 
  onFieldToggle, 
  onCategoryToggle 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState(new Set(['productInfo', 'contentFields', 'pricingData']));

  // Category labels and icons
  const categoryConfig = {
    productInfo: {
      label: 'Información del Producto',
      icon: 'Package',
      description: 'Identificadores y datos básicos'
    },
    catalogDetails: {
      label: 'Detalles del Catálogo',
      icon: 'BookOpen',
      description: 'Estado y información comercial'
    },
    contentFields: {
      label: 'Campos de Contenido',
      icon: 'FileText',
      description: 'Título, descripción y categorización'
    },
    pricingData: {
      label: 'Datos de Precios',
      icon: 'DollarSign',
      description: 'Precios, moneda y descuentos'
    },
    inventoryManagement: {
      label: 'Gestión de Inventario',
      icon: 'Archive',
      description: 'Stock, estado y multimedia'
    },
    publishingSettings: {
      label: 'Configuración de Publicación',
      icon: 'Settings',
      description: 'Tipo de publicación y opciones'
    },
    additionalData: {
      label: 'Datos Adicionales',
      icon: 'MoreHorizontal',
      description: 'Información complementaria y metadatos'
    }
  };

  // Filter fields based on search term
  const getFilteredFields = (fields) => {
    if (!searchTerm) return fields;
    return fields?.filter(field => 
      field?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      field?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase())
    );
  };

  // Check if category is fully selected
  const isCategoryFullySelected = (categoryFields) => {
    return categoryFields?.every(field => selectedFields?.has(field?.id));
  };

  // Check if category is partially selected
  const isCategoryPartiallySelected = (categoryFields) => {
    const selectedCount = categoryFields?.filter(field => selectedFields?.has(field?.id))?.length;
    return selectedCount > 0 && selectedCount < categoryFields?.length;
  };

  // Toggle category expansion
  const toggleCategory = (categoryKey) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded?.has(categoryKey)) {
      newExpanded?.delete(categoryKey);
    } else {
      newExpanded?.add(categoryKey);
    }
    setExpandedCategories(newExpanded);
  };

  // Handle select all/none for category
  const handleCategorySelection = (categoryKey, categoryFields) => {
    const isFullySelected = isCategoryFullySelected(categoryFields);
    onCategoryToggle(categoryFields, !isFullySelected);
  };

  return (
    <div className="space-y-6">
      {/* Search and Bulk Actions */}
      <div className="bg-surface border border-border rounded-lg p-6 shadow-soft">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-text-primary">
            Campos Disponibles
          </h2>
          
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onCategoryToggle(Object.values(availableFields)?.flat(), true)}
            >
              <Icon name="CheckSquare" size={16} className="mr-2" />
              Seleccionar Todo
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onCategoryToggle(Object.values(availableFields)?.flat(), false)}
            >
              <Icon name="Square" size={16} className="mr-2" />
              Deseleccionar Todo
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Icon name="Search" size={16} className="absolute left-3 top-3 text-text-secondary" />
          <Input
            type="text"
            placeholder="Buscar campos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Field Categories */}
      <div className="space-y-4">
        {Object.entries(availableFields)?.map(([categoryKey, categoryFields]) => {
          const config = categoryConfig?.[categoryKey];
          const filteredFields = getFilteredFields(categoryFields);
          const isExpanded = expandedCategories?.has(categoryKey);
          const isFullySelected = isCategoryFullySelected(categoryFields);
          const isPartiallySelected = isCategoryPartiallySelected(categoryFields);

          if (filteredFields?.length === 0 && searchTerm) return null;

          return (
            <div key={categoryKey} className="bg-surface border border-border rounded-lg shadow-soft overflow-hidden">
              {/* Category Header */}
              <div className="p-4 border-b border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <Checkbox
                      checked={isFullySelected}
                      indeterminate={isPartiallySelected}
                      onChange={() => handleCategorySelection(categoryKey, categoryFields)}
                    />
                    
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Icon name={config?.icon} size={16} className="text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-text-primary">
                          {config?.label}
                        </h3>
                        <p className="text-sm text-text-secondary">
                          {config?.description}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-text-secondary">
                      {categoryFields?.filter(f => selectedFields?.has(f?.id))?.length} / {categoryFields?.length}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleCategory(categoryKey)}
                    >
                      <Icon 
                        name="ChevronDown" 
                        size={16} 
                        className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                      />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Category Fields */}
              {isExpanded && (
                <div className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {filteredFields?.map((field) => (
                      <div 
                        key={field?.id}
                        className={`p-3 border border-border rounded-lg hover:bg-muted transition-colors ${
                          selectedFields?.has(field?.id) ? 'bg-primary/5 border-primary/20' : ''
                        }`}
                      >
                        <div className="flex items-start space-x-3">
                          <Checkbox
                            checked={selectedFields?.has(field?.id)}
                            onChange={() => onFieldToggle(field?.id)}
                            className="mt-0.5"
                          />
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-1">
                              <h4 className="font-medium text-text-primary truncate">
                                {field?.name}
                              </h4>
                              {field?.required && (
                                <span className="text-xs bg-error/10 text-error px-2 py-0.5 rounded-full">
                                  Requerido
                                </span>
                              )}
                              <span className="text-xs bg-muted text-text-secondary px-2 py-0.5 rounded-full">
                                {field?.type}
                              </span>
                            </div>
                            <p className="text-sm text-text-secondary">
                              {field?.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {filteredFields?.length === 0 && searchTerm && (
                    <div className="text-center py-8">
                      <Icon name="Search" size={48} className="text-text-secondary mx-auto mb-3" />
                      <p className="text-text-secondary">
                        No se encontraron campos que coincidan con "{searchTerm}"
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Preset Templates */}
      <div className="bg-surface border border-border rounded-lg p-6 shadow-soft">
        <h3 className="text-lg font-semibold text-text-primary mb-4">
          Plantillas Predefinidas
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const essentialFields = ['id', 'titulo', 'descripcion', 'precio', 'stock', 'estado', 'categoria'];
              onCategoryToggle(Object.values(availableFields)?.flat(), false);
              setTimeout(() => {
                const fieldsToSelect = Object.values(availableFields)?.flat()?.filter(f => essentialFields?.includes(f?.id));
                onCategoryToggle(fieldsToSelect, true);
              }, 100);
            }}
            className="justify-start"
          >
            <Icon name="Star" size={16} className="mr-2" />
            Esencial
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const fullFields = ['id', 'titulo', 'descripcion', 'precio', 'stock', 'estado', 'categoria', 'imagenes', 'tipoPublicacion'];
              onCategoryToggle(Object.values(availableFields)?.flat(), false);
              setTimeout(() => {
                const fieldsToSelect = Object.values(availableFields)?.flat()?.filter(f => fullFields?.includes(f?.id));
                onCategoryToggle(fieldsToSelect, true);
              }, 100);
            }}
            className="justify-start"
          >
            <Icon name="Package" size={16} className="mr-2" />
            Completo
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const requiredFields = Object.values(availableFields)?.flat()?.filter(f => f?.required);
              onCategoryToggle(Object.values(availableFields)?.flat(), false);
              setTimeout(() => {
                onCategoryToggle(requiredFields, true);
              }, 100);
            }}
            className="justify-start"
          >
            <Icon name="AlertCircle" size={16} className="mr-2" />
            Solo Requeridos
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FieldSelectionPanel;